<template>
  <div class="page-container">
    <template v-if="!loadingActiveSubscription">
      <div class="page-container-body">
        <h3 class="page-title">My Plan</h3>

        <div class="plan-badge d-inline-flex mb-8" v-if="isSubscriptionAdmin">
          You are admin of your Continuum Design {{ selectedPlan?.name }} plan.
        </div>
        <template v-else-if="!isEnterpriseUser">
          <div class="d-flex flex-column align-start ga-2 mb-8" v-if="subscriptionAccessType === 'FULL'">
            <div class="plan-badge">
              You need to contact the payment admin to make any changes to the plan.
            </div>
            <div class="plan-badge" v-if="activeSubscription?.payerUserEmail">
              <span class="font-weight-bold">Payment Admin:</span> {{ activeSubscription.payerUserEmail }}
            </div>
          </div>
        </template>

        <div class="plan-info">
          <div class="plan-info-header">
            <span class="planName">Continuum Design {{ selectedPlan?.name }}</span>
            <span class="planPrice" v-if="!isEnterpriseUser">${{
              selectedSubscriptionType === 'monthly' ? selectedPlan?.monthlyPrice : selectedPlan?.yearlyPrice
            }}/User/{{ selectedSubscriptionType === 'monthly' ? 'Month' : 'Year' }} (excluding tax)</span>
          </div>

          <div class="plan-features">
            <ul class="features">
              <li v-for="feature in selectedPlan?.features" :key="feature" class="featureItem">
                <v-icon size="large" color="var(--blue-3)">mds:check</v-icon>
                <span class="featureItemLabel">{{ feature }}</span>
              </li>
            </ul>

            <a href="https://www.qlerify.com/pricing" target="_blank" class="viewAllPlans">
              View all plans & features on the Pricing page.
            </a>
          </div>
        </div>
      </div>
      <div class="page-container-footer">
        <template v-if="isEnterpriseUser">
          <div class="plan-badge">
            <span>
              Please contact your Continuum Design Account Manager or submit a support request <a
                href="https://webforms.pipedrive.com/f/6NfI0XGDzXIe8nGmpN1ly8pQUw2YUXE6GnPVoWuCyliiWcg13YOiNmQBCeX3PF6nbJ"
                target="_blank" class="dialog-link">here</a> to make any changes to your plan
            </span>
          </div>
        </template>
        <template v-else>
          <template v-if="isSubscriptionAdmin">
            <div class="plan-badge" v-if="subscriptionAccessType === 'FULL'">
              <v-icon>mdi-calendar</v-icon>
              <span
                v-if="!!activeSubscription?.subscriptionDetails?.cancelAt && !activeSubscription?.scheduleSubscriptionDetails">
                Your subscription will expire on {{ nextBillingDate }}.
              </span>
              <span v-else>
                <template v-if="!!scheduledPlan">
                  Your next bill will be for the {{
                    scheduledPlan.name
                  }} plan and is scheduled for {{
                    scheduledNextBillingDate
                  }}. The payment of ${{
                    scheduledNextBillingAmount
                  }}  (excluding tax) will be charged to your card ending in **** {{
                    activeSubscription?.subscriptionDetails?.cardDetails?.lastFour
                  }}.
                </template>
                <template v-else>
                  Your next bill is ${{ activeSubscription?.subscriptionDetails?.nextBillingAmount }} (excluding tax)
                  on {{
                    nextBillingDate
                  }}. Your card ending in **** {{ activeSubscription?.subscriptionDetails?.cardDetails?.lastFour }} will be charged.
                </template>
              </span>
            </div>
            <v-spacer v-else/>
            <div class="d-flex align-center justify-center ga-2">
              <v-btn
                variant="tonal"
                size="large"
                color="var(--red-1)"
                @click="cancelSubscriptionDialogIsOpen = true"
                :loading="loadingCancelSubscription"
                v-if="subscriptionAccessType === 'FULL' && (!activeSubscription?.subscriptionDetails?.cancelAt || (!!activeSubscription?.subscriptionDetails?.cancelAt && activeSubscription.scheduleSubscriptionDetails))"
              >Cancel Plan
              </v-btn>
              <v-btn
                variant="flat"
                size="large"
                :loading="loadingRenew"
                v-if="subscriptionAccessType === 'LIMITED'"
                @click="onRenew"
              >{{ activeSubscription?.lastSubscriptionDetails?.status === 'past_due' ? 'Authenticate Payment' : 'Renew Plan' }}
              </v-btn>
              <v-btn
                variant="flat"
                size="large"
                @click="$refs.changePlanDialog.openDialog()"
              >
                {{
                  !!activeSubscription?.subscriptionDetails?.cancelAt &&
                    !activeSubscription?.scheduleSubscriptionDetails ? 'Reactivate' : 'Change Plan'
                }}
              </v-btn>
            </div>
          </template>
          <template v-else>
            <div class="plan-badge"
                 v-if="subscriptionAccessType === 'FULL' &&
                   !!activeSubscription?.subscriptionDetails?.cancelAt &&
                   !activeSubscription?.scheduleSubscriptionDetails">
              <v-icon>mdi-calendar</v-icon>
              <span>
                Your subscription will expire on {{ nextBillingDate }}.
              </span>
            </div>
          </template>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="page-loading-container-body">
        <v-progress-circular
          indeterminate
          color="white"
          size="32"
          width="2">
        </v-progress-circular>
        <span>Loading subscription...</span>
      </div>
    </template>

    <subscription-canceled-dialog ref="subscriptionCanceledDialog"></subscription-canceled-dialog>
    <change-plan-dialog ref="changePlanDialog"></change-plan-dialog>
    <info-message-dialog
      v-if="infoMessageDialogData"
      :is-open="infoMessageDialogData.isOpen"
      :title="infoMessageDialogData.title"
      :message="infoMessageDialogData.message"
      @close="infoMessageDialogData = null"
    />

    <cancel-subscription-dialog
      :is-open="cancelSubscriptionDialogIsOpen"
      @close="cancelSubscriptionDialogIsOpen = false"
      @onCancel="onCancelSubscriptionDialogClose"
      @onChangeSubscription="cancelSubscriptionDialogIsOpen = false;$refs.changePlanDialog.openDialog()"
    />
  </div>
</template>

<script>
import store from '@/store'
import format from 'date-fns/format'
import { mapState } from 'vuex'
import { loadStripe } from '@stripe/stripe-js'
import ChangePlanDialog from '@/components/ChangePlanDialog.vue'
import InfoMessageDialog from '@/components/InfoMessageDialog.vue'
import CancelSubscriptionDialog from '@/components/CancelSubscriptionDialog.vue'
import SubscriptionCanceledDialog from '@/components/SubscriptionCanceledDialog.vue'
import { ENTERPRISE_PLAN, getPlanByPriceId, getSubscriptionTypeByPriceId, getScheduledQuantity } from '@/utils/payments'

export default {
  components: {
    ChangePlanDialog,
    InfoMessageDialog,
    CancelSubscriptionDialog,
    SubscriptionCanceledDialog
  },
  computed: {
    ...mapState({
      currentUser: state => state.user.user,
      activeSubscription: state => state.payments.activeSubscription,
      subscriptionUsers: state => state.payments.subscriptionUsers,
      subscriptionAccessType: state => state.payments.subscriptionAccessType,
      loadingActiveSubscription: state => state.payments.loadingActiveSubscription,
      loadingCancelSubscription: state => state.payments.loadingCancelSubscription,
      isEnterpriseUser: state => state.payments.isEnterpriseUser,
      isSubscriptionAdmin: state => state.payments.isSubscriptionAdmin
    }),
    selectedPlan: function () {
      if (this.activeSubscription) {
        if (this.isEnterpriseUser) {
          return ENTERPRISE_PLAN
        }
        const priceId = this.activeSubscription?.subscriptionDetails?.price?.id || this.activeSubscription?.lastSubscriptionDetails?.price?.id
        return getPlanByPriceId(priceId)
      }
      return null
    },
    subscriptionType: function () {
      if (this.activeSubscription) {
        const priceId = this.activeSubscription?.subscriptionDetails?.price?.id || this.activeSubscription?.lastSubscriptionDetails?.price?.id
        return getSubscriptionTypeByPriceId(priceId)
      }
      return null
    },
    nextBillingDate: function () {
      const nextBillingDate = this.activeSubscription?.subscriptionDetails?.nextBillingDate
      if (nextBillingDate) {
        return format(new Date(nextBillingDate), 'MMMM dd, yyyy')
      }
      return ''
    },
    scheduledNextBillingDate: function () {
      const startDate = this.activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.startDate
      if (startDate) {
        return format(new Date(startDate), 'MMMM dd, yyyy')
      }
      return ''
    },
    scheduledPlan: function () {
      const scheduledPriceId = this.activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.items?.[0]?.priceId
      if (scheduledPriceId) {
        return getPlanByPriceId(scheduledPriceId)
      }
      return null
    },
    scheduledSubscriptionType: function () {
      const scheduledPriceId = this.activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.items?.[0]?.priceId
      if (scheduledPriceId) {
        return getSubscriptionTypeByPriceId(scheduledPriceId)
      }
      return null
    },
    scheduledNextBillingAmount: function () {
      const scheduleQuantity = getScheduledQuantity(this.activeSubscription)
      if (this.scheduledPlan) {
        const subscriptionType = this.scheduledSubscriptionType
        return scheduleQuantity * (subscriptionType === 'monthly' ? this.scheduledPlan.monthlyPrice : this.scheduledPlan.yearlyPrice)
      }
      return 0
    },
    selectedSubscriptionType: function () {
      return this.scheduledPlan ? this.scheduledSubscriptionType : this.subscriptionType
    }
  },
  data () {
    return {
      loadingRenew: false,
      infoMessageDialogData: null,
      cancelSubscriptionDialogIsOpen: false
    }
  },
  mounted () {
    this.checkAndOpenChangePlanDialog()
  },
  watch: {
    '$route.query.openChangePlan' (value) {
      if (value === 'true') {
        this.checkAndOpenChangePlanDialog()
      }
    }
  },
  methods: {
    checkAndOpenChangePlanDialog () {
      if (this.$route.query.openChangePlan === 'true') {
        this.$nextTick(() => {
          if (this.$refs.changePlanDialog) {
            this.$refs.changePlanDialog.openDialog()
            this.$router.replace({ query: {} })
          }
        })
      }
    },
    onCancelSubscriptionDialogClose () {
      this.cancelSubscriptionDialogIsOpen = false
      this.$refs.subscriptionCanceledDialog.openDialog()
    },
    async onRenew () {
      this.loadingRenew = true
      const { subscriptionId, status } = this.activeSubscription?.lastSubscriptionDetails || {}

      if (status === 'past_due') {
        const resetBillingCycleResponse = await store.dispatch('payments/resetBillingCycle', { subscriptionId })

        if (resetBillingCycleResponse?.success && resetBillingCycleResponse?.clientSecret) {
          // Existing clientSecret in response means that payment requires authentication via 3D Secure
          const stripeInstance = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
          const confirmCardResponse = await stripeInstance.confirmCardPayment(resetBillingCycleResponse.clientSecret)

          if (confirmCardResponse.error) {
            this.$store.commit('snackbar/showMessage', {
              content: 'Authentication failed',
              timeout: 6000,
              color: 'red',
              location: 'center center'
            }, { root: true })
          }
        }

        await store.dispatch('payments/getActiveSubscription')
        await store.dispatch('payments/getPayerUserSubscription')
        this.loadingRenew = false
        return
      }

      const lastPlanPriceId = this.activeSubscription?.lastSubscriptionDetails?.price?.id
      const users = [...(this.subscriptionUsers || []).map(u => ({ email: u.email }))]
      const createSubscriptionParams = {
        subscriptionPriceId: lastPlanPriceId,
        users
      }
      const createSubscriptionResponse = await store.dispatch('payments/createSubscription', createSubscriptionParams)
      this.loadingRenew = false
      if (createSubscriptionResponse.success) {
        this.open = false
        await store.dispatch('payments/getActiveSubscription')
        await store.dispatch('payments/getPayerUserSubscription')
      } else {
        this.infoMessageDialogData = {
          message: createSubscriptionResponse.message,
          isOpen: true
        }
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-container-body {
  flex-grow: 1;
  overflow: auto;
}

.page-loading-container-body {
  gap: 16px;
  height: 100%;
  display: flex;
  font-size: 16px;
  font-weight: 400;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  color: var(--dark-4, #363A40);
}

.page-container-footer {
  gap: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  margin-bottom: 32px;
  font-size: 20px;
  font-weight: 700;
  color: var(--dark-4, #363A40);
}

.plan-badge {
  gap: 8px;
  padding: 8px;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  border-radius: 8px;
  align-items: center;
  color: var(--dark-4, #363A40);
  background: var(--light-2, #F5F2F0);
}

.plan-info {
  padding: 24px;
}

.plan-info-header {
  gap: 24px;
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  justify-content: space-between;
}

.planName {
  font-size: 20px;
  font-weight: 400;
  color: var(--dark-4, #363A40);
}

.planPrice {
  font-size: 14px;
  font-weight: 500;
  color: var(--dark-2, #878F99);
}

.features {
  gap: 8px;
  margin: 0;
  padding: 0;
  display: flex;
  margin-bottom: 8px;
  flex-direction: column;
  list-style-type: none;
}

.featureItem {
  gap: 8px;
  display: flex;
}

.featureItemLabel {
  font-size: 14px;
  font-weight: 400;
  color: var(--dark-4, #363A40);
}

.viewAllPlans {
  font-size: 12px;
  font-weight: 400;
  color: var(--blue-3);
  padding-left: 32.5px;
  text-decoration: none;
}
</style>
